import type { Template, Generation, WizardData, Playbook, PlaybookSummary } from '../types/wizard';

const BASE_URL = '/api';

async function request<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || `Request failed: ${res.status}`);
  }

  return res.json();
}

// Templates
export async function fetchTemplates(params?: {
  category?: string;
  search?: string;
  sort?: string;
  page?: number;
  limit?: number;
}): Promise<{ templates: Template[]; total: number; page: number; pages: number }> {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  const res = await request<{ data: any[]; pagination: { total: number; page: number; totalPages: number } }>(
    `/templates${query ? `?${query}` : ''}`
  );
  return {
    templates: res.data.map(mapBackendTemplate),
    total: res.pagination.total,
    page: res.pagination.page,
    pages: res.pagination.totalPages,
  };
}

export async function fetchTemplate(id: string): Promise<Template> {
  const res = await request<{ data: any }>(`/templates/${id}`);
  return mapBackendTemplate(res.data);
}

export async function createTemplate(data: {
  name: string;
  description: string;
  category: string;
  tags: string[];
  orgId?: string;
  isPublic?: boolean;
  wizardData?: WizardData;
}): Promise<Template> {
  const res = await request<{ data: any }>('/templates', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      orgId: data.orgId || 'default-org',
    }),
  });
  return mapBackendTemplate(res.data);
}

export async function forkTemplate(id: string): Promise<Template> {
  const res = await request<{ data: any }>(`/templates/${id}/fork`, {
    method: 'POST',
    body: JSON.stringify({ orgId: 'default-org' }),
  });
  return mapBackendTemplate(res.data);
}

export async function starTemplate(id: string, action: 'star' | 'unstar' = 'star'): Promise<{ stars: number }> {
  const res = await request<{ data: { stars: number } }>(`/templates/${id}/star`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
  return res.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await request(`/templates/${id}`, { method: 'DELETE' });
}

// Template versions
export async function fetchVersions(templateId: string): Promise<any[]> {
  const res = await request<{ data: any[] }>(`/templates/${templateId}/versions`);
  return res.data;
}

export async function createVersion(templateId: string, data: {
  version: string;
  changelog: string;
  wizardAnswers: any;
  generatedConfig: any;
  files: any;
  createdBy: string;
}): Promise<any> {
  const res = await request<{ data: any }>(`/templates/${templateId}/versions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.data;
}

export async function fetchVersion(templateId: string, versionId: string): Promise<any> {
  const res = await request<{ data: any }>(`/templates/${templateId}/versions/${versionId}`);
  return res.data;
}

// Generations
export async function startGeneration(data: {
  name: string;
  wizardData: WizardData;
  templateId?: string;
}): Promise<Generation> {
  const res = await request<any>('/generations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  // Backend returns flat object, map to Generation type
  return {
    id: res.id,
    name: res.name,
    status: (res.status || 'pending').toLowerCase() as Generation['status'],
    createdAt: res.createdAt,
    projectContext: data.wizardData?.projectContext || { name: '', goal: '', description: '', industry: '', compliance: [] },
    wizardData: data.wizardData,
  };
}

export async function fetchGenerations(): Promise<Generation[]> {
  const res = await request<{ data: any[] }>('/generations');
  return res.data.map(mapBackendGeneration);
}

export async function fetchGeneration(id: string): Promise<Generation> {
  const res = await request<{ data: any }>(`/generations/${id}`);
  return mapBackendGeneration(res.data);
}

export async function getGenerationStatus(
  id: string
): Promise<{ status: string; progress: number }> {
  return request(`/generations/${id}/status`);
}

export async function downloadGeneration(id: string): Promise<Blob> {
  const url = `${BASE_URL}/generations/${id}/download`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Download failed');
  return res.blob();
}

export async function deleteGeneration(id: string): Promise<void> {
  await request(`/generations/${id}`, { method: 'DELETE' });
}

// Health check
export async function healthCheck(): Promise<{ status: string }> {
  return request('/health');
}

// Wizard options — single source of truth from backend/data/wizard-options.json
export async function fetchWizardOptions(): Promise<WizardOptions> {
  return request<WizardOptions>('/config/wizard-options');
}

export interface WizardOptions {
  projectContext: {
    projectGoals: Array<{ label: string; playbookId: string }>;
    industries: string[];
    complianceOptions: string[];
  };
  techStack: {
    languages: string[];
    frameworksByLang: Record<string, string[]>;
    databases: string[];
    cloudProviders: string[];
    infrastructureOptions: string[];
    packageManagers: string[];
    monorepoTools: string[];
  };
  teamStructure: {
    teamSizes: string[];
    claudeUserOptions: string[];
    timezoneOptions: string[];
  };
  gitWorkflow: {
    branchingStrategies: Array<{ value: string; label: string; desc: string }>;
    branchNamingConventions: Array<{ value: string; label: string; example: string }>;
    commitStandards: Array<{ value: string; label: string; example: string }>;
    prRequirementOptions: string[];
  };
  codingStandards: {
    lintingOptions: string[];
    formattingOptions: string[];
    testRequirementOptions: string[];
    architecturePatterns: string[];
    dependencyPolicies: string[];
    namingConventionOptions: string[];
  };
  cicd: {
    ciPlatforms: string[];
    pipelineStages: string[];
    autoDeployOptions: Array<{ value: string; label: string; desc: string }>;
  };
  mcpConfig: {
    ticketSystems: string[];
    documentationOptions: string[];
    communicationOptions: string[];
    sourceControlOptions: string[];
    cloudConsoleOptions: string[];
    monitoringOptions: string[];
  };
  plugins: {
    available: Array<{
      name: string;
      displayName: string;
      description: string;
      repo: string;
      category: string;
    }>;
  };
}

// Code Analysis
export interface CodeSample {
  filename: string;
  content: string;
}

export interface CodeAnalysisResult {
  suggestions: {
    linting: string | null;
    formatting: string | null;
    testRequirements: string[];
    coverageThreshold: number | null;
    architecture: string | null;
    sensitivePaths: string[];
    dependencyPolicy: string | null;
    maxFileLength: number | null;
    namingConventions: string[];
    customConventions: string;
  };
  reasoning: string;
  confidence: 'high' | 'medium' | 'low';
}

export async function analyzeCode(
  codeSamples: CodeSample[],
): Promise<CodeAnalysisResult> {
  const url = `${BASE_URL}/wizard/analyze-code`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ codeSamples }),
  });

  if (res.status === 503) {
    const body = await res.json();
    throw new Error(body.reason === 'bedrock_not_configured'
      ? 'AI analysis is not available. Please configure AWS Bedrock credentials.'
      : 'AI analysis unavailable');
  }

  if (!res.ok) {
    throw new Error('Analysis failed');
  }

  const body = await res.json();
  return body.data;
}

// Playbook API
export async function fetchPlaybooks(): Promise<PlaybookSummary[]> {
  const res = await fetch(`${BASE_URL}/playbooks`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.data || data || [];
}

export async function fetchPlaybook(id: string): Promise<Playbook | null> {
  const res = await fetch(`${BASE_URL}/playbooks/${id}`);
  if (!res.ok) return null;
  return res.json();
}

export async function fetchPlaybookForProjectType(projectType: string): Promise<Playbook | null> {
  const res = await fetch(`${BASE_URL}/playbooks/by-type/${encodeURIComponent(projectType)}`);
  if (!res.ok) return null;
  return res.json();
}

// Mapping helpers — transform backend shapes to frontend types
function mapBackendTemplate(t: any): Template {
  return {
    id: t.id,
    name: t.name,
    description: t.description,
    category: mapCategory(t.category),
    tags: t.tags || [],
    stars: t.stars || 0,
    forks: t._count?.forks ?? t.forks ?? 0,
    author: t.org?.name || t.author || 'CodeForge Team',
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
    techStack: extractTechStack(t),
    wizardData: t.latestVersion?.wizardAnswers || t.wizardData || ({} as WizardData),
    latestVersion: t.latestVersion || null,
  };
}

function mapBackendGeneration(g: any): Generation {
  const ctx = g.projectContext || {};
  return {
    id: g.id,
    name: g.name,
    status: (g.status || 'pending').toLowerCase() as Generation['status'],
    createdAt: g.createdAt,
    projectContext: {
      name: ctx.projectContext?.name || ctx.name || ctx.projectName || '',
      goal: ctx.projectContext?.goal || ctx.goal || '',
      description: ctx.projectContext?.description || ctx.description || '',
      industry: ctx.projectContext?.industry || ctx.industry || '',
      compliance: ctx.projectContext?.compliance || ctx.compliance || [],
    },
    wizardData: ctx as WizardData,
    outputFiles: g.outputFiles || undefined,
  };
}

function mapCategory(category: string): string {
  const map: Record<string, string> = {
    GREENFIELD: 'Frontend',
    LEGACY_MODERNIZATION: 'Backend',
    MICROSERVICES: 'Backend',
    MONOLITH: 'Full Stack',
    DATA_PIPELINE: 'Data/ML',
    MOBILE: 'Mobile',
    FULLSTACK: 'Full Stack',
    INFRASTRUCTURE: 'DevOps',
    CUSTOM: 'API',
  };
  return map[category] || category;
}

function extractTechStack(t: any): string[] {
  if (t.latestVersion?.wizardAnswers) {
    const w = t.latestVersion.wizardAnswers;
    const stack: string[] = [];
    if (w.techStack?.languages) stack.push(...w.techStack.languages);
    if (w.techStack?.frameworks) stack.push(...w.techStack.frameworks);
    return stack.length > 0 ? stack : t.tags || [];
  }
  return t.techStack || t.tags || [];
}
